import React, { useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { DeleteButton } from '@rd-web-markets/shared/dist/util/buttons';
import competentProfessionalsUsersService from '../../services/claim_project_reports/competent_professionals_users.service';
import { useTranslation } from 'react-i18next';
import { UserService } from '@rd-web-markets/shared/dist/services/user.service';
import debounce from 'lodash.debounce';
const competentProfessionalProjectFields = ['exclude_from_rnd_report', 'review_narrative', 'notes'];
const CompetentProfessionalListTableData = _ref => {
  let {
    body,
    setBody,
    saveData,
    rowData,
    deleteData,
    data,
    createNewData,
    removeEmptyRow,
    emptyRowId,
    tableHeadings,
    selectedProjectId
  } = _ref;
  const {
    t
  } = useTranslation();
  const [,, fetchUser] = UserService.useFetch({
    fetchImediately: false
  });
  const confirmDeletion = useCallback(async () => {
    if (rowData.email) {
      const existingUser = (await fetchUser({
        query: rowData.email
      })).users[0];
      if (existingUser) {
        return confirm(t('there_is_an_associated_user_to_this_competent_professional_deleting_the_competent_professional_wont_delete_the_user_do_you_want_to_proceed'));
      } else {
        return confirm(t('are_you_sure_you_want_to_delete_this_competent_professional'));
      }
    }
  }, [fetchUser, rowData.email, t]);
  const onDelete = useCallback(async function () {
    const confirmation = await confirmDeletion();
    if (confirmation || rowData.id.includes('new-project-cp')) {
      deleteData(...arguments);
    }
  }, [confirmDeletion, deleteData, rowData.id]);
  const createOrUpdateUser = competentProfessionalsUsersService.useCreateOrUpdateUser({
    claimProjectReportId: selectedProjectId,
    confirmUpdateMessage: t('updating_the_review_narrative_updates_the_permission_to_do_technical_review_on_the_user_associated_with_this_competent_professional_they_will_still_be_able_to_access_the_project'),
    confirmCreateMessage: t('checking_review_narrative_will_create_a_user_for_this_competent_professional_with_the_required_permissions_to_access_and_review_the_project_this_user_will_not_be_invited')
  });
  const debouncedSave = useMemo(() => debounce(body => saveData(body), 500), [saveData]);
  useEffect(() => {
    return () => debouncedSave.cancel();
  }, [debouncedSave]);
  const submit = () => {
    if (!!rowData.id) {
      saveData(body);
    } else {
      createNewData(new FormData());
    }
  };
  const getBodyValue = field => {
    if (competentProfessionalProjectFields.includes(field)) {
      const targetField = body.competent_professional_claim_project_reports?.find(r => r.claim_project_report_id === Number(selectedProjectId)) || body.competent_professional_claim_project_reports?.[0];
      if (targetField) {
        return targetField[field];
      }
    } else {
      return body[field];
    }
  };
  const getcompetentProfessionalProjectBody = body => {
    const competentProfessionalProjectIndex = body.competent_professional_claim_project_reports.findIndex(r => r.claim_project_report_id === Number(selectedProjectId));
    if (competentProfessionalProjectIndex === -1) {
      const newBody = {
        claim_project_report_id: Number(selectedProjectId)
      };
      body.competent_professional_claim_project_reports.push(newBody);
      return newBody;
    }
    return body.competent_professional_claim_project_reports[competentProfessionalProjectIndex];
  };
  const changeBody = e => {
    const key = e.target.name;
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const immediateSaveProperties = ['exclude_from_rnd_report', 'review_narrative', 'notes'];
    let newBody;
    if (competentProfessionalProjectFields.includes(key)) {
      const competentProfessionalProjectBody = getcompetentProfessionalProjectBody(body);
      competentProfessionalProjectBody[key] = value;
      newBody = {
        ...body
      };
    } else {
      newBody = {
        ...body,
        [key]: value
      };
    }
    if (immediateSaveProperties.includes(key) && !!rowData.id) {
      debouncedSave(newBody);
      setBody(newBody);
      return;
    }
    setBody(newBody);
  };
  const saveOnBlur = () => {
    const rowDataHasNames = !!rowData.id && rowData.name;
    const bodyHasNames = !rowData.id && body.name;
    if (rowDataHasNames || bodyHasNames) {
      submit();
    }
  };
  const renderTextInput = field => {
    const value = getBodyValue(field)?.trim();
    let buttonStyle = {};
    if (['name'].includes(field) && !value) {
      buttonStyle = {
        border: '2px solid red'
      };
    }
    return /*#__PURE__*/React.createElement(Form.Control, {
      style: buttonStyle,
      onBlur: saveOnBlur,
      value: getBodyValue(field),
      name: field,
      onChange: changeBody,
      size: "md",
      type: "text"
    });
  };
  const onAddReviewNarrativePermission = async e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const competentProfessional = await createOrUpdateUser(rowData, value);
    competentProfessional && setBody(competentProfessional);
  };
  const renderReviewNarrativeCheckbox = () => {
    return /*#__PURE__*/React.createElement(Form.Check, {
      type: "checkbox",
      name: 'review_narrative',
      checked: getBodyValue('can_do_project_technical_review'),
      className: 'text-start',
      onChange: onAddReviewNarrativePermission
    });
  };
  const renderFormCheck = field => {
    return /*#__PURE__*/React.createElement(Form.Check, {
      type: "checkbox",
      name: field,
      checked: getBodyValue(field),
      className: 'text-start',
      onChange: changeBody
    });
  };
  const renderTableData = fieldName => {
    const textFieldColumns = ['name', 'email', 'qualifications', 'relevant_experience', 'phone', 'notes'];
    const checkboxColumns = ['exclude_from_rnd_report', 'review_narrative'];
    if (textFieldColumns.includes(fieldName)) {
      return renderTextInput(fieldName);
    } else if (fieldName === 'review_narrative') {
      return renderReviewNarrativeCheckbox();
    } else if (checkboxColumns.includes(fieldName)) {
      return renderFormCheck(fieldName);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, tableHeadings.map(tableHeading => /*#__PURE__*/React.createElement("td", null, renderTableData(tableHeading))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DeleteButton, {
    style: {
      marginRight: '8px'
    },
    onClick: () => emptyRowId !== null ? removeEmptyRow(emptyRowId) : onDelete(body, data, selectedProjectId),
    "data-testid": "delete_button"
  }))));
};
export default CompetentProfessionalListTableData;